import React, { useEffect, useMemo, useState } from 'react';
import PageLoader from '../PageLoader';
import { axiosInstanceV2 } from '../../services/axios';
import { toast } from 'react-toastify';
import { usePayment } from '../../hooks/usePayment';
import { useDispatch } from 'react-redux';
import PricingCard from '../../pages/subscriptionPlan/components/PricingCard';
import { useNavigate, useParams } from 'react-router-dom';
import { setBillingType, setPlan } from '../../store/reducers/subscriptionSlice/subscriptionSlice';
import useSubscription from '../../hooks/useSubscription';
import { usePricingPlans } from '../../hooks/usePricingPlans';
import useSubUrl from '../../hooks/useSubUrl';
import { fetchUserInfo } from '../../store/action/userInfo';
import { previewPlanChange } from '../../services/subscription';
import { sortPlansByDisplayOrder } from '../../utils/planUtils';
import '../../pages/subscriptionPlan/subscription-plan.scss';

/**
 * @param onPlanChanged optional — called after a plan change that did NOT need
 *   checkout, so the host page can refetch anything it owns locally (the
 *   subscription settings page keeps its own copy of /subscription/details).
 */
function PricingModal({ isOpen, handleClose, onPlanChanged }) {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const { suburl: suburlParam } = useParams();
	const suburl = useSubUrl();
	const activeSuburl = suburlParam || suburl;
	const { openSubscriptionCheckout } = usePayment();
	const {
		subscription,
		isSubscriptionFree,
		isSubscriptionRecurring,
		subscriptionBillingPeriod,
		subscriptionPaymentMethod,
	} = useSubscription();
	const { plans, loading } = usePricingPlans({ enabled: isOpen });
	const [billing, setBilling] = useState(subscriptionBillingPeriod ?? 'monthly');
	const [confirmModal, setConfirmModal] = useState(null);
	const [processing, setProcessing] = useState(false);
	const [isVerifyingPayment, setIsVerifyingPayment] = useState(false);
	// Dry-run figures for the confirmation screen. null while in flight, and
	// stays null if the call fails — the screen degrades to its old wording
	// rather than blocking a plan change on a read-only request.
	const [preview, setPreview] = useState(null);
	const [previewLoading, setPreviewLoading] = useState(false);

	// handle plan selection
	const handleSelectPlan = (plan, billingDetails, type) => {
		setConfirmModal({
			plan,
			billingDetails,
			type,
		});

		// Free users never reach /change-plan (they're routed to checkout to
		// enter billing details first), and previewPlanChange blocks that case
		// for the same reason — so there is nothing to preview.
		if (isSubscriptionFree || !subscription?.plan?.id) {
			setPreview(null);
			return;
		}

		setPreview(null);
		setPreviewLoading(true);
		previewPlanChange({
			razorpay_plan_id: billingDetails.razorpay_plan_id,
			billing_period: billingDetails.period,
		})
			.then(setPreview)
			.catch((err) => {
				// Non-fatal: the confirm button still works. Losing the quote is
				// better than refusing to let someone change plan because a
				// read-only endpoint hiccuped.
				console.error('Plan change preview failed:', err);
				setPreview(null);
			})
			.finally(() => setPreviewLoading(false));
	};

	// Paise -> rupees. Every amount the API returns is in the smallest unit.
	const rupees = (paise) =>
		`₹${(Number(paise ?? 0) / 100).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

	// confirm upgrade
	const handleConfirmPlanChange = async () => {
		setProcessing(true);
		const { plan, billingDetails } = confirmModal;
		// Route to checkout whenever there is no PAID subscription to change —
		// i.e. the user is on Free, or has no subscription at all. Those users
		// have no Razorpay customer and no billing details yet, so /change-plan
		// cannot work; they must go through checkout to enter billing details and
		// pick recurring vs one-time.
		//
		// This condition used to read `!isSubscriptionFree && !isSubscribed`,
		// which is inverted for the Free case. It only appeared to work because
		// isSubscriptionFree was permanently false (it read plans.is_free, a
		// column that does not exist). Once that was corrected, Free users
		// started falling through to /change-plan and hit
		// "Razorpay customer not found."
		//
		// It then read `!isSubscribed`, which is organisation.is_subscribed — a
		// denormalised column written only by the Razorpay webhook. Whenever that
		// column drifted from reality (a missed webhook is enough), a paying org
		// took this branch and was sent to checkout WITHOUT /change-plan ever
		// being called. From the outside that looks like the button doing nothing.
		//
		// Derived from the subscription itself instead. A real plan always carries
		// a plan id; an org with no subscription has `plan: null`. Being slightly
		// too permissive here is safe — the backend answers CHECKOUT_REQUIRED and
		// the handler below routes to checkout anyway. Being too strict is not,
		// because it skips the request silently.
		const hasPaidPlan = Boolean(subscription?.plan?.id);
		if (isSubscriptionFree || !hasPaidPlan) {
			dispatch(setPlan(plan));
			dispatch(setBillingType(billing));
			setConfirmModal(null);
			handleClose();
			// PricingModal is only reachable from within the already-authenticated
			// app, so a suburl is almost always known here — this is the
			// "existing org, currently unsubscribed/free" resubscribe path.
			navigate(activeSuburl ? `/user/${activeSuburl}/checkout` : '/checkout');
			setProcessing(false);
			return;
		}

		let result = null;

		try {
			const authToken = localStorage.getItem('authToken');
			const response = await axiosInstanceV2.post(
				'/subscription/change-plan',
				{
					razorpay_plan_id: billingDetails.razorpay_plan_id,
					billing_period: billingDetails.period,
					is_recurring: isSubscriptionRecurring,
				},
				{ headers: { Authorization: `Bearer ${authToken}` } },
			);

			result = response.data.data;

			// The backend now tells us whether a payment window is needed, instead of
			// the client inferring it from the change type.
			//
			// A RECURRING upgrade no longer opens checkout at all: Razorpay charges
			// the prorated difference against the mandate already on file, and only
			// applies the plan change if that charge succeeds. Opening a payment
			// window here would ask the customer to pay a second time.
			//
			// Checkout is still required where there is no mandate to charge — a
			// one-time subscription, or an org coming off the Free plan.
			if (result.requires_checkout) {
				try {
					await openSubscriptionCheckout({
						subscription: result.subscription,
						billingDetails: result.billing_details,
						// Preference only — Razorpay still offers both card and UPI, so
						// an upgrade is no longer forced onto whatever method the
						// original subscription happened to use.
						paymentMethod: subscriptionPaymentMethod ?? null,
						// Runs only once the payment is actually confirmed. This owns
						// the ENTIRE success path for the checkout branch — the
						// announce-and-close block further down is skipped, because at
						// that point the payment is still in flight.
						//
						// Closing has to happen here for the same reason: an earlier
						// version returned without it, so a successful payment left the
						// confirmation modal sitting open over the upgraded page with
						// nothing to dismiss it.
						onProcessing: () => {
							setConfirmModal(null);
							setIsVerifyingPayment(true);
						},
						onSuccess: () => {
							setIsVerifyingPayment(false);
							setProcessing(false);
							// Both modals must be torn down BEFORE navigating. The pricing
							// modal is rendered by the settings page, so leaving either one
							// mounted meant the customer landed back on My Subscription with
							// the confirmation dialog still sitting open over it — the
							// payment had succeeded but the screen still asked them to pay.
							setConfirmModal(null);
							handleClose();
							onPlanChanged?.();

							// Same destination the first-time purchase uses (see
							// usePayment's initiatePayment). An upgrade is just as much a
							// completed payment, but it used to end silently on the settings
							// page with no receipt and no confirmation that anything had
							// been charged. /payment-success reads the subscription back
							// from the server, so it shows the NEW plan, the payment id, the
							// amount and the billing dates without needing anything passed
							// through the router.
							// The success page reads everything else from the server, but it
							// cannot tell an upgrade from a first purchase — both end with a
							// paid, active subscription. This is the only piece of context
							// that has to travel with the navigation.
							//
							// This branch (requires_checkout) is also how a DOWNGRADE reaches
							// checkout — a card mandate can't be repriced in place, so
							// #scheduleDowngradeViaReplacement (services.js) asks the customer
							// to reauthorise a new, lower mandate now, deferred to take over at
							// the current term's end. That used to always set
							// `upgradedToPlanName`, so a customer who just downgraded landed
							// here reading "Subscription upgraded to Starter successfully" —
							// wrong on both counts: it is a downgrade, and nothing has taken
							// effect yet (they keep their current plan until cutover). Route on
							// the backend's own `result.type` instead of assuming every
							// checkout-requiring change is an upgrade.
							navigate('/payment-success', {
								replace: true,
								state:
									result.type === 'upgrade'
										? { upgradedToPlanName: plan.name }
										: {
												downgradeScheduledToPlanName: plan.name,
												downgradeEffectiveDate: result.effective_date
											}
							});
						},
						// usePayment already tells the user the payment was
						// cancelled/declined; this just clears the spinner so the modal
						// is usable again for a retry.
						onDismiss: () => {
							setProcessing(false);
							setIsVerifyingPayment(false);
						},
						onVerificationError: () => {
							setIsVerifyingPayment(false);
							setProcessing(false);
						},
					});
				} catch (checkoutErr) {
					console.error('Checkout error:', checkoutErr);
					toast.error('Payment window failed to open. Please try again.');
					return;
				}

				// Everything below announces a COMPLETED change and tears the modal
				// down. Neither is true yet: `openSubscriptionCheckout` returns as
				// soon as the Razorpay window opens, so the payment is still in
				// flight. Saying "You're now on Professional" here — then leaving no
				// correction when the customer closes the window — is how a
				// cancelled upgrade came to look like a successful one.
				//
				// Both outcomes are handled by the callbacks above instead:
				// onSuccess closes and announces, onDismiss re-enables the buttons.
				// Nothing may run here, or it would fire while the customer is still
				// looking at the Razorpay window.
				return;
			}

			// `applies_at` is the backend's own statement of timing, so the wording
			// cannot drift from what actually happened. It also distinguishes a
			// recurring renewal from a non-recurring expiry, which the PRD requires
			// we never conflate.
			const appliedImmediately = result.applies_at === 'immediately';

			toast.success(
				result.message ??
				(appliedImmediately
					? `You're now on ${plan.name}.`
					: `Change to ${plan.name} scheduled for ${new Date(result.effective_date).toLocaleDateString()}`),
			);

			setConfirmModal(null);
			handleClose();

			// Refresh unless checkout is still in flight — an immediate upgrade has
			// already changed the org's limits, and a scheduled change needs the
			// banner to appear. The checkout path refreshes from its own success
			// handler once payment completes.
			//
			// This was `window.location.reload()`, which tore the page down before
			// the toast above could be read. A downgrade compounded it: the change
			// is scheduled for cycle end, so the reloaded page looked identical and
			// a change that HAD succeeded was indistinguishable from nothing
			// happening at all. Refetching in place keeps the message on screen and
			// still gives every consumer of userInfo the new state.
			if (!result.requires_checkout) {
				await dispatch(fetchUserInfo());
				onPlanChanged?.();
			}
		} catch (error) {
			console.error('Plan change error:', error);
			const status = error?.response?.status;
			const backendMessage = error?.response?.data?.message;
			const backendCode = error?.response?.data?.code;

			if (!error?.response) {
				toast.error('Network error — check your connection and try again.');
			} else if (status === 401) {
				toast.error('Your session has expired. Please log in again.');
			} else if (backendCode === 'CHECKOUT_REQUIRED') {
				// Safety net: the org has no billing set up, so /change-plan can
				// never succeed. Send them through checkout instead of showing a
				// dead-end error.
				dispatch(setPlan(plan));
				dispatch(setBillingType(billing));
				setConfirmModal(null);
				handleClose();
				navigate(activeSuburl ? `/user/${activeSuburl}/checkout` : '/checkout');
				return;
			} else {
				toast.error(backendMessage ?? 'Failed to update subscription. Please try again.');
			}
			// CHANGED: modal stays open on failure — no setConfirmModal(null) here,
			// so the user can retry without reselecting the plan
		} finally {
			setProcessing(false); // CHANGED: this alone in finally now — no result access here
		}
	};

	const filteredPlans = useMemo(() => {
		const filtered = plans
			// The Free plan is normally excluded here — there's no self-serve
			// downgrade-to-Free flow wired into this modal (onOpenFreeModal is
			// only handled by the standalone subscription-plan page, and isn't
			// passed to PricingCard below). The one exception: when Free
			// genuinely IS the org's current plan, showing NO card as "current"
			// is worse than showing a disabled Free card — that was the bug
			// (every Free-plan org opening "Change Plan" saw no current-plan
			// marker anywhere, see backend-node's test_current_plan_badge.js).
			.filter((plan) => plan.key !== 'free' || plan.is_current)
			// Free structurally has no billing entries (see plans/services.js —
			// only rows with a razorpay_plan_id get one), so it can never match
			// the "has an entry for the selected period" check below. It only
			// ever reaches here already gated by is_current above, so let it
			// through unconditionally.
			.filter(
				(plan) =>
					plan.key === 'free' ||
					plan.billing?.some((billingPlan) => billingPlan.period === billing),
			);

		return sortPlansByDisplayOrder(filtered);
	}, [plans, billing]);

	useEffect(() => {
		if (!isOpen) return;
		setBilling(subscriptionBillingPeriod || 'monthly');
	}, [isOpen, subscriptionBillingPeriod]);

	if (!isOpen) return null;

	return (
		<>
			<div
				className="modal fade show d-block pricing-plan-modal"
				tabIndex="-1"
				aria-modal="true"
				role="dialog"
				style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
			>
				<div className="modal-dialog modal-dialog-top modal-xl pricing-modal-dialog">
					<div className="modal-content">
						<div className="modal-header">
							<h5 className="modal-title">Change Subscription Plan</h5>
							<button
								type="button"
								className="btn-close"
								onClick={handleClose}
							/>
						</div>

						<div className="d-flex justify-content-center align-items-center py-4">
							<div className="billing-toggle">
								<button
									type="button"
									className={billing === 'daily' ? 'active' : ''}
									onClick={() => setBilling('daily')}
								>
									Daily
								</button>

								<button
									type="button"
									className={billing === 'weekly' ? 'active' : ''}
									onClick={() => setBilling('weekly')}
								>
									Weekly
								</button>


								<button
									className={billing === 'monthly' ? 'active' : ''}
									onClick={() => setBilling('monthly')}
								>
									Monthly
								</button>
								<button
									className={billing === 'yearly' ? 'active' : ''}
									onClick={() => setBilling('yearly')}
								>
									Yearly
								</button>
							</div>
						</div>

						<div className="modal-body w-100">
							{loading ? (
								<PageLoader />
							) : filteredPlans.length === 0 ? (
								<div className="text-center py-5">
									<h5>No Plans Available</h5>
									<p className="text-muted mb-0">
										No {billing} subscription plans are currently available.
									</p>
								</div>
							) : (
								<div className="pricing-grid pricing-modal-grid">
									{filteredPlans.map((plan) => {
										const billingDetails = plan.billing.find((item) => item.period === billing);
										return (
											<PricingCard
												key={plan._id || plan.key}
												plan={plan}
												billing={billing}
												billingDetails={billingDetails}
												currentPlanKey={subscription?.plan?.key}
												currentRazorpayPlanId={subscription?.plan?.razorpay_plan_id}
												// Per-billing-entry, so the label tracks the monthly/yearly
												// toggle. The card-level value describes only the cycle the
												// user is currently on, which made the other cycle's button
												// read wrongly — most visibly "Downgrade Plan" on an annual
												// switch that is charged immediately as an upgrade.
												changeType={billingDetails?.change_type ?? plan.change_type}
												onSelectPlan={handleSelectPlan}
											/>
										);
									})}
								</div>
							)}
						</div>
						<div className="modal-footer">
							<button
								type="button"
								className="btn-cancel"
								onClick={handleClose}
							>
								Close
							</button>
						</div>
					</div>
				</div>
			</div>

			{/* confirmation modal */}
			{confirmModal && (
				<div
					className="modal fade show d-block"
					style={{ backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 1060 }}
				>
					<div className="modal-dialog modal-dialog-centered">
						<div className="modal-content">
							<div className="modal-header">
								<h5 className="modal-title">
									{isSubscriptionFree
										? 'Subscribe to Plan'
										: confirmModal.type === 'upgrade'
											? 'Confirm Upgrade'
											: 'Confirm Downgrade'}
								</h5>
								<button
									type="button"
									className="btn-close"
									onClick={() => setConfirmModal(null)}
								/>
							</div>

							<div className="modal-body">
								{isSubscriptionFree ? (
									<p>
										You're subscribing to the
										<strong> {confirmModal.plan.name}</strong> plan. Click Continue to provide your
										billing details before completing payment.
									</p>
								) : (
									<>
										{confirmModal.type === 'upgrade' ? (
											<p>
												After successful payment, your
												<strong> {confirmModal.plan.name}</strong> plan will become active
												immediately.
											</p>
										) : (
											// Was "You're upgrading to X for ₹<full price>" on the
											// DOWNGRADE branch: wrong direction, and quoting the target
											// plan's full price for a change that charges nothing today.
											<p>
												You're moving down to <strong>{confirmModal.plan.name}</strong> (
												{confirmModal.billingDetails.period}). You keep your current plan until it
												ends — nothing is charged today.
											</p>
										)}

										{previewLoading && <p className="text-muted mb-0">Calculating amount…</p>}

										{preview && (
											<div className="mt-3">
												<div className="d-flex justify-content-between py-1">
													<span className="text-muted">Current plan</span>
													<span>
														{preview.current_plan?.name}
														{preview.current_plan?.billing_period
															? ` (${preview.current_plan.billing_period})`
															: ''}
													</span>
												</div>
												<div className="d-flex justify-content-between py-1">
													<span className="text-muted">
														{confirmModal.type === 'upgrade' ? 'New plan' : 'Downgraded plan'}
													</span>
													<span>
														{preview.target_plan?.name}
														{preview.target_plan?.billing_period
															? ` (${preview.target_plan.billing_period})`
															: ''}
													</span>
												</div>

												{/* The number the whole screen exists for. `null` means we
												    genuinely do not compute it (Razorpay prices the
												    recurring case at charge time) — showing ₹0.00 there
												    would be a lie, so the note explains it instead. */}
												{preview.amount_due_now !== null &&
													preview.amount_due_now !== undefined && (
														<div className="d-flex justify-content-between py-1 fw-bold">
															<span>
																{preview.amount_is_estimate
																	? 'Estimated amount payable now'
																	: 'Amount payable now'}
															</span>
															<span>
																{preview.amount_is_estimate ? '≈ ' : ''}
																{rupees(preview.amount_due_now)}
															</span>
														</div>
													)}

												{preview.change_type === 'upgrade' && preview.amount_due_now > 0 && (
													<div className="text-muted fs-6">
														Prorated for the remaining days of your current term — you're charged
														only the difference, and your expiry date does not change.
														{preview.amount_is_tax_inclusive ? ' Inclusive of tax.' : ''}
													</div>
												)}

												{preview.renewal_amount ? (
													<div className="d-flex justify-content-between py-1">
														<span className="text-muted">Renews at</span>
														<span>{rupees(preview.renewal_amount)}</span>
													</div>
												) : null}

												{preview.effective_date && (
													<div className="d-flex justify-content-between py-1">
														<span className="text-muted">Effective from</span>
														<span>{new Date(preview.effective_date).toLocaleDateString()}</span>
													</div>
												)}

												{preview.prorated_amount_note && (
													<div className="text-muted fs-6 mt-2">{preview.prorated_amount_note}</div>
												)}

												{/*
												  Required by both journeys in the business rules:
												  "Feature or limit changes" on the upgrade screen, and
												  "features, limits, or access that will be reduced" plus
												  "any usage that currently exceeds the lower plan limit"
												  on the downgrade screen. The API returned both all
												  along — this screen just never rendered them, so people
												  confirmed downgrades without seeing what they'd lose.
												*/}
												{preview.feature_changes?.length > 0 && (
													<div className="mt-3">
														<div className="fw-semibold mb-1">
															{preview.change_type === 'upgrade'
																? "What you'll gain"
																: 'What changes'}
														</div>
														{preview.feature_changes.slice(0, 6).map((change) => (
															<div
																key={change.key}
																className="d-flex justify-content-between py-1 fs-6"
															>
																<span className="text-muted">{change.feature}</span>
																<span>
																	{change.from ?? '—'} →{' '}
																	<strong>{change.to ?? 'Not included'}</strong>
																</span>
															</div>
														))}
														{preview.feature_changes.length > 6 && (
															<div className="text-muted fs-6">
																+{preview.feature_changes.length - 6} more
															</div>
														)}
													</div>
												)}

												{preview.usage_over_new_limits?.length > 0 && (
													<div
														className="mt-3 p-2 rounded-2"
														style={{ background: '#FFF4E5', border: '1px solid #F5B97F' }}
													>
														<div className="fw-semibold mb-1">
															You&apos;re currently over the new plan&apos;s limits
														</div>
														{preview.usage_over_new_limits.map((breach) => (
															<div
																key={breach.key}
																className="d-flex justify-content-between py-1 fs-6"
															>
																<span>{breach.feature}</span>
																<span>
																	using <strong>{breach.used}</strong> of {breach.new_limit}
																</span>
															</div>
														))}
													</div>
												)}

												{preview.blocked_reason && (
													<div className="text-danger fs-6 mt-2">{preview.blocked_reason}</div>
												)}
											</div>
										)}
									</>
								)}
							</div>

							<div className="modal-footer">
								<button
									type="button"
									className="btn-cancel"
									onClick={() => setConfirmModal(null)}
								>
									Cancel
								</button>
								<button
									disabled={processing}
									type="button"
									className="btn-primary-modal"
									onClick={handleConfirmPlanChange}
								>
									{isSubscriptionFree
										? 'Continue'
										: confirmModal.type === 'upgrade'
											? 'Proceed to Payment'
											: 'Confirm Downgrade'}
								</button>
							</div>
						</div>
					</div>
				</div>
			)}

			{/* Payment processing full-page white loader with centered spinner & text */}
			{isVerifyingPayment && (
				<div
					className="position-fixed top-0 start-0 w-100 vh-100 bg-white d-flex flex-column justify-content-center align-items-center text-center p-3"
					style={{ zIndex: 99999 }}
				>
					<span
						className="Page_loader"
						role="status"
						aria-hidden="true"
					></span>
					<h4 className="mt-4 fw-bold text-dark mb-2">Processing Payment...</h4>
					<p className="text-muted mb-0 fs-6">
						Confirming your upgrade. Please do not close or refresh this window.
					</p>
				</div>
			)}
		</>
	);
}

export default PricingModal;
